/**
 * Centralized link/config surface for the Studio Desktop landing pages.
 *
 * Components must never hardcode installer URLs, the web-app origin, or legacy
 * `*.dc.html` hrefs from the source export. Everything funnels through here so
 * staging vs prod targets are env-driven and the thank-you download hand-off
 * has a single source of truth.
 *
 * URLs are env-driven (`VITE_*`, surfaced to the bundle by `next.config.mjs`)
 * with production defaults so the app boots locally without any env setup.
 */

/** Operating systems we ship a desktop installer for. */
export const DESKTOP_OS = ['mac', 'windows'] as const
export type DesktopOs = (typeof DESKTOP_OS)[number]

export function isDesktopOs(value: string | null | undefined): value is DesktopOs {
  return value === 'mac' || value === 'windows'
}

/** Base origin of the idea.ai web app. */
export const STUDIO_APP_URL: string = process.env.VITE_STUDIO_APP_URL ?? 'https://www.idea.ai'

/**
 * Canonical origin where these landing pages are served. Used for
 * `metadataBase`, canonical URLs, JSON-LD, robots, and the sitemap.
 *
 * Canonicals live under `/desktop` on www.idea.ai. This is a
 * bare origin so `siteUrl('/desktop')` resolves cleanly. Env-driven so
 * staging/preview deploys emit their own origin (`VITE_STUDIO_DESKTOP_LP_URL`).
 */
export const SITE_URL: string = process.env.VITE_STUDIO_DESKTOP_LP_URL ?? 'https://www.idea.ai'

/** Absolute URL for a landing-page path (e.g. `siteUrl('/desktop/free-tts')`). */
export function siteUrl(path = '/'): string {
  return new URL(path, SITE_URL).toString()
}

// Defaults are this app's own redirect routes, which resolve the latest
// installer from the release pipeline's latest-downloads.json at request time
// (see app/api/download/[os]/route.ts) — so download links never need
// per-release updates. Root-relative hrefs work for both the thank-you page's
// auto-start (`window.location.href`) and plain anchors.
const INSTALLER_URLS: Record<DesktopOs, string> = {
  mac: process.env.VITE_STUDIO_DESKTOP_DOWNLOAD_MAC_URL ?? '/api/download/mac',
  windows: process.env.VITE_STUDIO_DESKTOP_DOWNLOAD_WINDOWS_URL ?? '/api/download/windows',
}

/** Absolute installer download URL for the given OS. */
export function installerUrl(os: DesktopOs): string {
  return INSTALLER_URLS[os]
}

/** Absolute URL into the Studio web app, e.g. `appUrl('/flow')`. */
export function appUrl(path = '/'): string {
  return new URL(path, STUDIO_APP_URL).toString()
}
